/**
 * AutopilotWizard — Geführter Monats-Autopilot
 *
 * Der Kunde gibt nur an:
 *   Schritt 1: Monat + Intensität (leicht / normal / intensiv)
 *   Schritt 2: Hauptziele + optionale Schwerpunkte
 *
 * Das System entscheidet automatisch:
 *   - Wie viele Videos, Karussells und Beiträge erstellt werden
 *   - Wie die Inhaltsarten (Wissen, Vertrauen, Interaktion, Angebote) verteilt werden
 *   - Auf welche Tage die Posts fallen
 *
 * Technische Details (Longcat, Pillar-Namen etc.) sind vollständig verborgen.
 */

import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { NavLink } from "react-router-dom";
import {
  Sparkles, ChevronRight, ChevronLeft, Loader2, CheckCircle,
  Calendar, Zap, Target, Info, ChevronDown, ArrowRight
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Konfiguration ─────────────────────────────────────────────────────────────

const MONTH_NAMES = [
  "", "Januar", "Februar", "März", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Dezember",
];

const INTENSITIES = [
  {
    id: "leicht",
    label: "Entspannt",
    sub: "ca. 2 Posts/Woche",
    posts: 8,
    description: "Gut für den Einstieg — regelmäßig präsent ohne Zeitdruck.",
    color: "border-green-300 bg-green-50 text-green-800",
    activeColor: "border-green-500 bg-green-100 ring-2 ring-green-300",
    dot: "bg-green-500",
  },
  {
    id: "normal",
    label: "Regelmäßig",
    sub: "ca. 3 Posts/Woche",
    posts: 12,
    description: "Der solide Standard — sichtbar ohne Stress.",
    color: "border-fox-fire/40 bg-orange-50 text-orange-800",
    activeColor: "border-fox-fire bg-orange-100 ring-2 ring-fox-fire/30",
    dot: "bg-fox-fire",
  },
  {
    id: "intensiv",
    label: "Aktiv",
    sub: "ca. 5 Posts/Woche",
    posts: 20,
    description: "Maximale Sichtbarkeit — ideal wenn du gerade wächst.",
    color: "border-purple-300 bg-purple-50 text-purple-800",
    activeColor: "border-purple-500 bg-purple-100 ring-2 ring-purple-300",
    dot: "bg-purple-500",
  },
];

const GOALS = [
  {
    id: "Sichtbarkeit",
    emoji: "👁️",
    label: "Sichtbarkeit",
    desc: "Mehr Menschen sollen dich finden und kennenlernen.",
  },
  {
    id: "Vertrauen",
    emoji: "🤝",
    label: "Vertrauen aufbauen",
    desc: "Zeigen, dass du verlässlich und kompetent bist.",
  },
  {
    id: "Buchungen",
    emoji: "📅",
    label: "Mehr Buchungen",
    desc: "Anfragen und Buchungen für dein Coaching.",
  },
  {
    id: "Community",
    emoji: "💬",
    label: "Community stärken",
    desc: "Austausch, Interaktion und echte Verbindungen.",
  },
  {
    id: "Events",
    emoji: "🎯",
    label: "Events / Aktionen",
    desc: "Ein Event, Webinar oder eine Aktion bewerben.",
  },
];

const SCHWERPUNKTE = [
  { id: "Wissen", emoji: "📚", label: "Wissen & Tipps" },
  { id: "Angebote", emoji: "🎁", label: "Angebote zeigen" },
  { id: "Testimonials", emoji: "⭐", label: "Erfolgsgeschichten" },
  { id: "Behind-the-Scenes", emoji: "🎬", label: "Einblicke" },
];

// ── Erklärungs-Box ────────────────────────────────────────────────────────────

const ExplanationBox = ({ expanded, onToggle }) => (
  <div className="rounded-xl border border-stone-200 bg-stone-50 overflow-hidden">
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between p-4 text-left hover:bg-stone-100 transition-colors"
    >
      <span className="flex items-center gap-2 text-sm font-semibold text-stone-700">
        <Info size={15} className="text-stone-400" />
        So entsteht dein Monatsplan
      </span>
      <ChevronDown
        size={15}
        className={`text-stone-400 transition-transform ${expanded ? "rotate-180" : ""}`}
      />
    </button>

    {expanded && (
      <div className="px-4 pb-4 space-y-3 text-sm text-stone-600">
        <p className="leading-relaxed">
          Wir achten automatisch auf eine gute Mischung aus <strong>Wissensinhalten,
          Vertrauensaufbau, Interaktion und Angeboten</strong> — damit dein Auftritt
          abwechslungsreich wirkt und nicht nur aus Werbung besteht.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {[
            {
              icon: "📚",
              title: "Mehrwert / Wissen",
              text: "Tipps und Erklärungen — zeigen, dass du Experte bist.",
            },
            {
              icon: "🤝",
              title: "Vertrauen",
              text: "Erfolgsgeschichten und Einblicke — stärken Glaubwürdigkeit.",
            },
            {
              icon: "💬",
              title: "Interaktion",
              text: "Fragen und Impulse — fördern Kommentare und Austausch.",
            },
            {
              icon: "🎯",
              title: "Angebote",
              text: "Einladungen und CTAs — sorgen für konkrete Anfragen.",
            },
          ].map((item) => (
            <div key={item.title} className="flex gap-2 p-2.5 bg-white rounded-lg border border-stone-100">
              <span className="text-base flex-shrink-0">{item.icon}</span>
              <div>
                <p className="font-semibold text-stone-700 text-xs">{item.title}</p>
                <p className="text-stone-500 text-xs leading-relaxed">{item.text}</p>
              </div>
            </div>
          ))}
        </div>
        <p className="text-stone-500 text-xs leading-relaxed">
          Videos, Karussells und klassische Beiträge werden automatisch abwechslungsreich
          verteilt — du musst nichts festlegen. Jeden Entwurf kannst du später
          anpassen oder löschen.
        </p>
      </div>
    )}
  </div>
);

// ── Plan-Zusammenfassung nach Generierung ─────────────────────────────────────

const PlanSummary = ({ result, onClose }) => {
  if (!result) return null;

  const pillars = result.content_pillar_labels || {};
  const formats = result.format_labels || {};

  const PILLAR_COLORS = {
    "Mehrwert":    "bg-blue-100 text-blue-700",
    "Vertrauen":   "bg-green-100 text-green-700",
    "Interaktion": "bg-amber-100 text-amber-700",
    "Angebote":    "bg-purple-100 text-purple-700",
  };

  const FORMAT_COLORS = {
    "Beitrag":   "bg-stone-100 text-stone-600",
    "Karussell": "bg-cyan-100 text-cyan-700",
    "Video":     "bg-violet-100 text-violet-700",
  };

  return (
    <div className="rounded-2xl border border-green-200 bg-green-50 p-6 space-y-5">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 bg-green-500 rounded-xl flex items-center justify-center flex-shrink-0">
          <CheckCircle size={20} className="text-white" />
        </div>
        <div>
          <h3 className="font-bold text-green-900 text-lg">
            Dein Plan für {result.month_name} {result.year} ist fertig
          </h3>
          <p className="text-sm text-green-700 mt-0.5">
            {result.imported_count} Entwürfe wurden im Kalender gespeichert.
          </p>
        </div>
      </div>

      {/* Zusammenfassungstext */}
      <p className="text-sm text-green-800 leading-relaxed bg-white rounded-xl p-4 border border-green-200">
        {result.summary}
      </p>

      {/* Analytics-Hinweis — nur wenn Performance-Daten genutzt wurden */}
      {result.used_analytics && (
        <div className="flex items-start gap-2.5 bg-white rounded-xl p-3.5 border border-green-200">
          <span className="text-base flex-shrink-0">✨</span>
          <p className="text-xs text-green-700 leading-relaxed">
            <strong>Dein Plan orientiert sich an deinen besten Inhalten der letzten Wochen.</strong>{" "}
            Inhalte und Formate, die gut ankommen, werden bevorzugt — automatisch und ohne
            dass du etwas einstellen musst.
          </p>
        </div>
      )}

      {/* Inhaltsmix-Badges */}
      <div>
        <p className="text-xs font-semibold text-green-700 uppercase tracking-wide mb-2">
          Inhaltsarten
        </p>
        <div className="flex flex-wrap gap-2">
          {Object.entries(pillars).map(([label, count]) => (
            <span
              key={label}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${
                PILLAR_COLORS[label] || "bg-stone-100 text-stone-600"
              }`}
            >
              {label}
              <span className="font-bold">{count}×</span>
            </span>
          ))}
        </div>
      </div>

      {/* Formatmix-Badges */}
      <div>
        <p className="text-xs font-semibold text-green-700 uppercase tracking-wide mb-2">
          Formate
        </p>
        <div className="flex flex-wrap gap-2">
          {Object.entries(formats).map(([label, count]) => (
            count > 0 && (
              <span
                key={label}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${
                  FORMAT_COLORS[label] || "bg-stone-100 text-stone-600"
                }`}
              >
                {label}
                <span className="font-bold">{count}×</span>
              </span>
            )
          ))}
        </div>
      </div>

      {/* CTAs */}
      <div className="flex flex-wrap gap-3 pt-1">
        <NavLink
          to="/monthly-plan"
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-xl font-semibold text-sm hover:bg-green-700 transition-colors"
        >
          <Calendar size={15} />
          Kalender öffnen
        </NavLink>
        <button
          onClick={onClose}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-white text-green-700 border border-green-300 rounded-xl font-medium text-sm hover:bg-green-50 transition-colors"
        >
          Neuen Plan erstellen
        </button>
      </div>
    </div>
  );
};

// ── Hauptkomponente ───────────────────────────────────────────────────────────

const AutopilotWizard = ({ onSuccess }) => {
  const now = new Date();
  const [step, setStep] = useState(1);
  const [month, setMonth] = useState(now.getMonth() + 2 <= 12 ? now.getMonth() + 2 : 1);
  const [year, setYear] = useState(now.getMonth() + 2 <= 12 ? now.getFullYear() : now.getFullYear() + 1);
  const [intensity, setIntensity] = useState("normal");
  const [goals, setGoals] = useState(["Sichtbarkeit"]);
  const [schwerpunkte, setSchwerpunkte] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [explanationOpen, setExplanationOpen] = useState(false);

  const toggleGoal = (id) => {
    setGoals((prev) =>
      prev.includes(id) ? prev.filter((g) => g !== id) : [...prev, id]
    );
  };

  const toggleSchwerpunkt = (id) => {
    setSchwerpunkte((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const handleGenerate = async () => {
    if (!goals.length) {
      toast.error("Bitte wähle mindestens ein Ziel");
      return;
    }

    setGenerating(true);
    try {
      const res = await axios.post(`${API}/autopilot/generate-monthly-plan`, {
        month,
        year,
        goals,
        intensity,
        schwerpunkte,
        platforms: ["instagram", "facebook", "linkedin"],
      });

      if (res.data.success) {
        setResult(res.data);
        toast.success(`${res.data.imported_count} Entwürfe für ${res.data.month_name} erstellt!`);
        onSuccess?.();
      } else {
        toast.error("Plan konnte nicht erstellt werden");
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Verbindungsfehler";
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  };

  const handleReset = () => {
    setResult(null);
    setStep(1);
  };

  // ── Ergebnis-Ansicht ────────────────────────────────────────────────────────
  if (result) {
    return <PlanSummary result={result} onClose={handleReset} />;
  }

  return (
    <div className="space-y-5">
      {/* Header mit Step-Indikator */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-xl font-bold text-stone-900 flex items-center gap-2">
            <Sparkles size={20} className="text-fox-fire" />
            Monat automatisch planen
          </h2>
          <p className="text-sm text-stone-500 mt-0.5">
            Wir erstellen dir einen abwechslungsreichen Monatsplan — du musst nur
            dein Ziel angeben.
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          {[1, 2].map((s) => (
            <div
              key={s}
              className={`w-2.5 h-2.5 rounded-full transition-all ${
                s === step ? "bg-fox-fire scale-110" : s < step ? "bg-green-400" : "bg-stone-200"
              }`}
            />
          ))}
        </div>
      </div>

      {/* ── Schritt 1: Monat + Intensität ──────────────────────────────────── */}
      {step === 1 && (
        <div className="space-y-5">
          {/* Monat + Jahr */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1.5">
                Monat
              </label>
              <select
                value={month}
                onChange={(e) => setMonth(Number(e.target.value))}
                className="w-full border border-stone-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-fox-fire/30 focus:border-fox-fire"
              >
                {MONTH_NAMES.slice(1).map((name, i) => (
                  <option key={i + 1} value={i + 1}>{name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1.5">
                Jahr
              </label>
              <select
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                className="w-full border border-stone-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-fox-fire/30 focus:border-fox-fire"
              >
                {[now.getFullYear(), now.getFullYear() + 1].map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Intensität */}
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">
              Wie viel möchtest du im Monat posten?
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {INTENSITIES.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setIntensity(item.id)}
                  className={`text-left p-4 rounded-xl border-2 transition-all ${
                    intensity === item.id ? item.activeColor : item.color
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`w-2 h-2 rounded-full ${item.dot}`} />
                    <span className="font-bold text-sm">{item.label}</span>
                  </div>
                  <p className="text-xs font-medium opacity-80">{item.sub}</p>
                  <p className="text-xs opacity-60 mt-1 leading-relaxed">{item.description}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Erklärungs-Box */}
          <ExplanationBox
            expanded={explanationOpen}
            onToggle={() => setExplanationOpen(!explanationOpen)}
          />

          {/* Weiter */}
          <button
            onClick={() => setStep(2)}
            className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-fox-fire text-white rounded-xl font-semibold text-sm hover:bg-orange-600 transition-colors"
          >
            Weiter: Ziele festlegen
            <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* ── Schritt 2: Ziele + Schwerpunkte ────────────────────────────────── */}
      {step === 2 && (
        <div className="space-y-5">
          {/* Ziele */}
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">
              Was soll dein Plan vor allem erreichen?
              <span className="text-stone-400 font-normal ml-1">(mehrere möglich)</span>
            </label>
            <div className="space-y-2">
              {GOALS.map((goal) => {
                const active = goals.includes(goal.id);
                return (
                  <button
                    key={goal.id}
                    onClick={() => toggleGoal(goal.id)}
                    className={`w-full flex items-center gap-3 p-3.5 rounded-xl border-2 text-left transition-all ${
                      active
                        ? "border-fox-fire bg-orange-50"
                        : "border-stone-200 bg-white hover:border-stone-300"
                    }`}
                  >
                    <span className="text-xl flex-shrink-0">{goal.emoji}</span>
                    <div className="flex-1 min-w-0">
                      <p className={`font-semibold text-sm ${active ? "text-fox-fire" : "text-stone-700"}`}>
                        {goal.label}
                      </p>
                      <p className="text-xs text-stone-500 leading-relaxed">{goal.desc}</p>
                    </div>
                    {active && (
                      <div className="w-5 h-5 rounded-full bg-fox-fire flex items-center justify-center flex-shrink-0">
                        <CheckCircle size={12} className="text-white" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Schwerpunkte (optional) */}
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">
              Gibt es Inhalte, die du besonders zeigen möchtest?
              <span className="text-stone-400 font-normal ml-1">(optional)</span>
            </label>
            <div className="grid grid-cols-2 gap-2">
              {SCHWERPUNKTE.map((s) => {
                const active = schwerpunkte.includes(s.id);
                return (
                  <button
                    key={s.id}
                    onClick={() => toggleSchwerpunkt(s.id)}
                    className={`flex items-center gap-2 p-3 rounded-xl border-2 text-left transition-all ${
                      active
                        ? "border-stone-400 bg-stone-100"
                        : "border-stone-200 bg-white hover:border-stone-300"
                    }`}
                  >
                    <span className="text-base">{s.emoji}</span>
                    <span className={`text-sm font-medium ${active ? "text-stone-800" : "text-stone-600"}`}>
                      {s.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Zusammenfassung */}
          <div className="rounded-xl bg-stone-50 border border-stone-200 p-4 text-sm text-stone-600">
            <p className="font-semibold text-stone-700 mb-1">Dein Plan auf einen Blick:</p>
            <p>
              <strong>{MONTH_NAMES[month]} {year}</strong> ·{" "}
              {INTENSITIES.find((i) => i.id === intensity)?.sub} ·{" "}
              {INTENSITIES.find((i) => i.id === intensity)?.posts} Entwürfe
            </p>
            <p className="mt-0.5 text-xs text-stone-400">
              Wir kümmern uns automatisch um eine gute Mischung aus Formaten und Inhalten.
            </p>
          </div>

          {/* Buttons */}
          <div className="flex gap-3">
            <button
              onClick={() => setStep(1)}
              className="flex items-center gap-1.5 px-4 py-2.5 text-stone-600 bg-stone-100 hover:bg-stone-200 rounded-xl font-medium text-sm transition-colors"
            >
              <ChevronLeft size={15} />
              Zurück
            </button>
            <button
              onClick={handleGenerate}
              disabled={generating || !goals.length}
              className="flex-1 flex items-center justify-center gap-2 px-5 py-2.5 bg-fox-fire text-white rounded-xl font-semibold text-sm hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {generating ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Erstelle Monatsplan…
                </>
              ) : (
                <>
                  <Zap size={16} />
                  Plan automatisch erstellen
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AutopilotWizard;
